import { Label, Textarea, TextInput } from "flowbite-react";
import { forwardRef, InputHTMLAttributes, ReactNode, useState } from "react";
import {
  Control,
  Controller,
  FieldError,
  FieldErrorsImpl,
  Merge,
} from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { CustomButton } from "./CustomButton";

interface CustomInputProps
  extends InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  name?: string;
  control?: Control<any>;
  error?:
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | string
    | boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  placeholder?: string;
  type?:
    | "text"
    | "number"
    | "url"
    | "email"
    | "hidden"
    | "password"
    | "date"
    | "datetime-local";
  defaultValue?: string;
  grayBackground?: boolean;
  label?: string;
  labelVariant?: "label" | "title" | "none";
  labelClassName?: string;
  hint?: ReactNode;
  rightComponent?: ReactNode;
  rules?: any;
  onValueChange?: (e: any) => void;
  rows?: number;
  inputDir?: "ltr" | "rtl" | "auto" | "";
  inputClassName?: string;
}

export const CustomInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  CustomInputProps
>(
  (
    {
      name = "",
      control,
      error,
      helperText,
      className = "",
      placeholder = "",
      type = "text",
      defaultValue,
      grayBackground,
      label,
      labelVariant = "label",
      labelClassName,
      hint,
      rightComponent,
      rules,
      onValueChange,
      rows,
      inputDir = "",
      inputClassName,
      ...rest
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
      <div className="w-full space-y-1" dir="auto">
        {label && (
          <Label
            htmlFor={name}
            className={twMerge(
              labelVariant === "none"
                ? ""
                : labelVariant === "label"
                ? "block text-sm font-medium text-gray-700"
                : "block mb-3 text-sm font-medium text-gray-700",
              labelClassName
            )}
          >
            {label}
            {rest.required && (
              <span className="ml-0.5 text-gray-400" aria-hidden="true">
                *
              </span>
            )}
          </Label>
        )}

        <div className="flex flex-row gap-1 relative">
          <div className="flex flex-col w-full">
            <Controller
              name={name}
              control={control}
              defaultValue={defaultValue ?? ""}
              rules={rules}
              render={({ field }) =>
                rows && rows > 1 ? (
                  <Textarea
                    {...field}
                    ref={ref as React.Ref<HTMLTextAreaElement>}
                    id={name}
                    rows={rows}
                    placeholder={placeholder}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      field.onChange(e);
                      onValueChange?.(e);
                    }}
                    onBlur={(e) => {
                      field.onBlur();
                      rest.onBlur?.(e);
                    }}
                    color={
                      error ? "failure" : grayBackground ? undefined : "gray"
                    }
                    className={twMerge(
                      "w-full shadow-sm rounded-md",
                      className,
                      inputClassName
                    )}
                    {...rest}
                    {...(inputDir !== "" ? { dir: inputDir } : {})}
                  />
                ) : type === "date" || type === "datetime-local" ? (
                  <div className="relative w-full">
                    <input
                      {...field}
                      ref={ref as React.Ref<HTMLInputElement>}
                      id={name}
                      placeholder={placeholder}
                      type={type}
                      value={field.value ?? ""}
                      onChange={(e) => {
                        field.onChange(e);
                        onValueChange?.(e);
                      }}
                      onBlur={(e) => {
                        field.onBlur();
                        rest.onBlur?.(e);
                      }}
                      className={twMerge(
                        "block w-full border relative",
                        error
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "disabled:cursor-not-allowed disabled:opacity-50 bg-white text-gray-900 placeholder-gray-400 rounded-md p-2.5 md:py-2 text-base md:text-sm shadow-sm",
                        className,
                        inputClassName
                      )}
                      {...rest}
                      {...(inputDir !== "" ? { dir: inputDir } : {})}
                    />
                  </div>
                ) : (
                  <TextInput
                    {...field}
                    ref={ref as React.Ref<HTMLInputElement>}
                    id={name}
                    placeholder={placeholder}
                    type={
                      type !== "password"
                        ? type
                        : showPassword
                        ? "text"
                        : "password"
                    }
                    value={field.value ?? ""}
                    onChange={(e) => {
                      field.onChange(e);
                      onValueChange?.(e);
                    }}
                    onBlur={(e) => {
                      field.onBlur();
                      rest.onBlur?.(e);
                    }}
                    color={
                      error ? "failure" : grayBackground ? undefined : "gray"
                    }
                    className={twMerge("w-full shadow-sm", className)}
                    theme={
                      inputClassName
                        ? {
                            field: {
                              input: { withShadow: { off: inputClassName } },
                            },
                          }
                        : undefined
                    }
                    {...rest}
                    {...(inputDir !== "" ? { dir: inputDir } : {})}
                  />
                )
              }
            />
          </div>

          {type === "password" && !rows && (
            <CustomButton
              variant="none"
              size="icon"
              className="absolute inset-y-0 right-2 h-auto"
              onClick={() => setShowPassword(!showPassword)}
              icon={showPassword ? "eyeOff" : "eye"}
            />
          )}

          {rightComponent}
        </div>

        {hint}

        {helperText && (
          <p dir="auto" className="text-xs text-red-600">
            {typeof helperText === "string"
              ? helperText?.toString()
              : helperText?.message?.toString() ?? ""}
          </p>
        )}
      </div>
    );
  }
);

CustomInput.displayName = "CustomInput";
